/**
 * Client ของการตามสอบ Lot — พูดกับสองเส้นทางของ `docs/API_CONTRACT.md` (D-073)
 * และ **ไม่พูดกับเส้นทางอื่นเลย**
 *
 * หลักการเดียวกับ `lib/goodsReceipts/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "ไม่มี edge" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "lot นี้ไม่มีความสัมพันธ์" —
 *   รายการว่างกับอ่านไม่ได้ต้องแยกกันให้ขาด และที่นี่มันสำคัญเป็นพิเศษ: หน้าจอที่
 *   บอกว่า lot ปนเปื้อนไม่ได้ไปไหนต่อทั้งที่อ่านคำตอบไม่ออก คือของที่ไม่ถูกเรียกคืน
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 *
 * **สิ่งที่ไฟล์นี้ไม่มีโดยเจตนา: เส้นทางเขียนทุกชนิด** — edge ของ `lot_genealogy`
 * ถูกเขียนโดยธุรกรรม Scan-Out เท่านั้นและโดยอย่างอื่นไม่ได้เลย (D-065) API ไม่มี
 * route ที่เขียน edge และจะไม่มี การมีฟังก์ชันเขียนที่นี่จึงเป็นคำสัญญาที่ backend
 * ไม่เคยให้ไว้ และเป็นประตูสู่ genealogy ที่หายไปได้ทั้งที่ของเคลื่อนจริง
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import {
  GENEALOGY_BACKWARD,
  GENEALOGY_FORWARD,
  type GenealogyDirection,
  type LotGenealogy,
  type LotGenealogyEdge,
  type LotGenealogyLot,
  type LotGenealogyQuery,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const LOTS_PATH = '/v1/lots';

/**
 * ประกอบ path ของทิศทางหนึ่ง — **สองเส้นทาง ไม่ใช่เส้นเดียวที่มี `?direction=`**
 *
 * contract แจกแจงมันเป็นสอง endpoint และฝั่งเซิร์ฟเวอร์ก็ลงทะเบียนเป็นสอง route
 * การรวบเป็น query parameter ที่นี่จะทำให้ client ยิงไปยัง URL ที่ไม่มีอยู่จริง
 */
export function lotGenealogyPath(lotId: string, direction: GenealogyDirection): string {
  return `${LOTS_PATH}/${encodeURIComponent(lotId)}/genealogy/${direction}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ **สำหรับกลุ่มนี้เท่านั้น**
 *
 * ไม่มี `conflict` และไม่มี `rule-violation` ในชุดนี้โดยเจตนา: ที่นี่ไม่มีอะไรเขียน
 * จึงไม่มีกฎทางธุรกิจให้ชนและไม่มีมุมมองให้เก่า (`router.py` — `_GENEALOGY_RESPONSES`
 * ก็ประกาศไว้แค่ `400`/`401`/`403`/`404` ด้วยเหตุผลเดียวกัน) สถานะที่ประกาศไว้เกิน
 * จริงคือข้อความที่ไม่มีวันแสดง และเป็นกิ่งที่ไม่มีใครเคยเห็นว่าถูกหรือผิด
 *
 * ⚠️ `not-found` ของกลุ่มนี้กลืนสองความหมายเข้าด้วยกัน **โดยตั้งใจฝั่งเซิร์ฟเวอร์**:
 * lot ที่ไม่มีอยู่จริง กับ lot ที่มีอยู่แต่อยู่นอกโรงงานของผู้เรียก — ต่างจาก
 * Goods Receipt ที่นอก scope ตอบ `403` เพราะที่นั่น `plant_code` เดินทางไปกับคำขอ
 * ส่วนที่นี่ lot ถูกอ้างด้วย id ลำพัง การแยกสองกรณีจึงเท่ากับยืนยันว่า lot นี้มีอยู่
 * ในโรงงานที่ผู้เรียกมองไม่เห็น ซึ่งเป็นการรั่วข้อมูลจากเลขบนป้ายที่ใครก็อ่านได้
 */
export type LotGenealogyFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type LotGenealogyResult =
  | { readonly status: 'ready'; readonly genealogy: LotGenealogy }
  | LotGenealogyFailure;

export interface LotGenealogyRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* --------------------------------------------------------------- query ---- */

/** ต่อ query string — มีแค่ `limit` กับ `cursor` และไม่มีตัวกรองอื่นใน contract */
export function buildGenealogyQuery(query: LotGenealogyQuery = {}): string {
  const params = new URLSearchParams();
  if (typeof query.limit === 'number' && Number.isInteger(query.limit)) {
    params.append('limit', String(query.limit));
  }
  if (typeof query.cursor === 'string' && query.cursor.trim() !== '') {
    params.append('cursor', query.cursor.trim());
  }
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  return raw as Record<string, unknown>;
}

/** lot ที่ถูกถาม — สี่ฟิลด์ที่ contract บังคับ ครบทุกตัวหรือไม่รับเลย */
export function parseLotGenealogyLot(raw: unknown): LotGenealogyLot | null {
  const record = asRecord(raw);
  if (record === null) {
    return null;
  }

  const lotId = requiredString(record.lot_id);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const qualityStatus = requiredString(record.quality_status);

  if (lotId === null || productId === null || productCode === null || qualityStatus === null) {
    return null;
  }
  return {
    lot_id: lotId,
    product_id: productId,
    product_code: productCode,
    quality_status: qualityStatus,
  };
}

/**
 * หนึ่ง edge — **บังคับครบทั้งเจ็ดฟิลด์** ไม่ผ่อนให้ตัวใดหายไปได้
 *
 * ⚠️ `qty_consumed` ผ่าน `requiredString` ไม่ใช่ตัวแปลงตัวเลข ด้วยเหตุผลเดียวกับ
 * `parseGoodsReceipt()`: `NUMERIC(18,4)` ที่ผ่าน `Number` มาแล้วคือค่าที่เพี้ยนไป
 * ก่อนถึงที่นี่ การรับไว้คือการกลืนมัน — และ edge ที่ไม่บอกปริมาณ คือ edge ที่ตอบ
 * ไม่ได้ว่าของปนเปื้อนไปกี่กิโลกรัม
 */
export function parseLotGenealogyEdge(raw: unknown): LotGenealogyEdge | null {
  const record = asRecord(raw);
  if (record === null) {
    return null;
  }

  const lot = parseLotGenealogyLot(record);
  const qtyConsumed = requiredString(record.qty_consumed);
  const uom = requiredString(record.uom);
  const confirmationId = requiredString(record.operation_confirmation_id);

  if (lot === null || qtyConsumed === null || uom === null || confirmationId === null) {
    return null;
  }
  return {
    ...lot,
    qty_consumed: qtyConsumed,
    uom,
    operation_confirmation_id: confirmationId,
  };
}

/**
 * อ่าน `{ lot, edges, next_cursor }` — **และจากรูปนี้เท่านั้น**
 *
 * `edges` ที่เป็น array ว่างผ่านได้ (เป็นคำตอบปกติ) แต่ payload ที่ **ไม่มีคีย์
 * `edges`** ไม่ผ่าน: สองอย่างนี้ต่างกันตรงที่อย่างหลังแปลว่าเราอ่านคำตอบผิดรูป แล้ว
 * แสดงมันเป็น "ไม่พบความสัมพันธ์" ซึ่งบนหน้าจอเรียกคืนสินค้าคือคำตอบที่ผิดที่สุด
 */
export function parseLotGenealogy(raw: unknown): LotGenealogy | null {
  const record = asRecord(raw);
  if (record === null || !Array.isArray(record.edges)) {
    return null;
  }

  const lot = parseLotGenealogyLot(record.lot);
  if (lot === null) {
    return null;
  }

  const edges: LotGenealogyEdge[] = [];
  for (const entry of record.edges) {
    const parsed = parseLotGenealogyEdge(entry);
    if (parsed === null) {
      return null;
    }
    edges.push(parsed);
  }

  const cursor = nullableString(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }
  // cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
  // จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
  return { lot, edges, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  const record = asRecord(raw);
  if (record === null) {
    return EMPTY_ENVELOPE;
  }
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | LotGenealogyFailure;

async function requestPayload(
  path: string,
  options: LotGenealogyRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/**
 * `GET /v1/lots/{lot_id}/genealogy/{direction}` — หนึ่ง hop ในทิศทางที่ขอ
 *
 * `direction` เป็นพารามิเตอร์ของฟังก์ชันเดียว ไม่ใช่สองฟังก์ชันที่คัดลอกกัน เพราะ
 * contract บอกว่าคำตอบเหมือนกันทุกอย่าง — แต่ **path ยังเป็นสอง path จริง** ดู
 * `lotGenealogyPath()`
 */
export async function fetchLotGenealogy(
  lotId: string,
  direction: GenealogyDirection,
  query: LotGenealogyQuery = {},
  options: LotGenealogyRequestOptions = {},
): Promise<LotGenealogyResult> {
  const result = await requestPayload(
    `${lotGenealogyPath(lotId, direction)}${buildGenealogyQuery(query)}`,
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const genealogy = parseLotGenealogy(result.payload);
  return genealogy === null ? { status: 'invalid-response' } : { status: 'ready', genealogy };
}

/** `…/genealogy/backward` — lot ต้นทางที่ถูกใช้มาเป็น lot นี้ (root cause) */
export function fetchBackwardGenealogy(
  lotId: string,
  query?: LotGenealogyQuery,
  options?: LotGenealogyRequestOptions,
): Promise<LotGenealogyResult> {
  return fetchLotGenealogy(lotId, GENEALOGY_BACKWARD, query, options);
}

/** `…/genealogy/forward` — lot ปลายทางที่ผลิตจาก lot นี้ (การเรียกคืนสินค้า) */
export function fetchForwardGenealogy(
  lotId: string,
  query?: LotGenealogyQuery,
  options?: LotGenealogyRequestOptions,
): Promise<LotGenealogyResult> {
  return fetchLotGenealogy(lotId, GENEALOGY_FORWARD, query, options);
}
